import { useCallback, useEffect, useRef, useState } from 'react';
import { Room, RoomEvent, Track, type Participant, type RemoteTrack } from 'livekit-client';
import { useGetChatCallTokenMutation } from '../../store/api/chatCallsApi';

export type EstadoSala = 'apagada' | 'conectando' | 'conectada' | 'reconectando' | 'sin-configurar' | 'error';

/**
 * LA SALA DE AUDIO/VIDEO de la llamada del chat (fase 3, 14/10/2026), con LiveKit.
 *
 * Vive en el layout (LlamadaDelChat), no en la pantalla del chat: la llamada sigue sonando aunque
 * navegues por el CRM. Conecta cuando estoy adentro de una llamada viva y se desconecta cuando
 * termina o salgo. Arranca con micrófono prendido y cámara apagada.
 *
 * ⛔ El audio de los demás se engancha a <audio> sueltos en el body (no dentro de un componente
 * que se desmonta): minimizar la pantalla no puede cortar el sonido.
 * ⛔ iPhone/Safari no deja sonar audio sin un toque del usuario: si el navegador lo bloquea,
 * `audioBloqueado` queda en true y la UI muestra "Tocá para escuchar" (→ `activarAudio`).
 */
export function useSalaDeLlamada(callId: string | null) {
  const [pedirPase] = useGetChatCallTokenMutation();
  const roomRef = useRef<Room | null>(null);
  const audiosRef = useRef(new Map<string, HTMLMediaElement[]>());
  const [estado, setEstado] = useState<EstadoSala>('apagada');
  const [, setTick] = useState(0);
  const [audioBloqueado, setAudioBloqueado] = useState(false);
  const [hablando, setHablando] = useState<Set<string>>(new Set());
  const refrescar = useCallback(() => setTick((t) => t + 1), []);

  useEffect(() => {
    if (!callId) return;
    let cancelado = false;
    const room = new Room({ adaptiveStream: true, dynacast: true });
    roomRef.current = room;
    setEstado('conectando');

    const engancharAudio = (track: RemoteTrack, _pub: unknown, p: Participant) => {
      if (track.kind !== Track.Kind.Audio) { refrescar(); return; }
      const el = track.attach();
      el.style.display = 'none';
      document.body.appendChild(el);
      audiosRef.current.set(p.identity + track.sid, [el]);
      refrescar();
    };
    const soltarAudio = (track: RemoteTrack, _pub: unknown, p: Participant) => {
      const els = audiosRef.current.get(p.identity + track.sid) ?? [];
      track.detach().forEach((e) => e.remove());
      els.forEach((e) => e.remove());
      audiosRef.current.delete(p.identity + track.sid);
      refrescar();
    };

    room
      .on(RoomEvent.TrackSubscribed, engancharAudio)
      .on(RoomEvent.TrackUnsubscribed, soltarAudio)
      .on(RoomEvent.ParticipantConnected, refrescar)
      .on(RoomEvent.ParticipantDisconnected, refrescar)
      .on(RoomEvent.TrackMuted, refrescar)
      .on(RoomEvent.TrackUnmuted, refrescar)
      .on(RoomEvent.LocalTrackPublished, refrescar)
      .on(RoomEvent.LocalTrackUnpublished, refrescar)
      .on(RoomEvent.ActiveSpeakersChanged, (ps: Participant[]) => setHablando(new Set(ps.map((p) => p.identity))))
      .on(RoomEvent.AudioPlaybackStatusChanged, () => setAudioBloqueado(!room.canPlaybackAudio))
      .on(RoomEvent.Reconnecting, () => setEstado('reconectando'))
      .on(RoomEvent.Reconnected, () => setEstado('conectada'))
      .on(RoomEvent.Disconnected, () => { if (!cancelado) setEstado('apagada'); });

    (async () => {
      try {
        const { url, token } = await pedirPase(callId).unwrap();
        if (cancelado) return;
        await room.connect(url, token);
        if (cancelado) { await room.disconnect(); return; }
        setEstado('conectada');
        setAudioBloqueado(!room.canPlaybackAudio);
        await room.localParticipant.setMicrophoneEnabled(true).catch(() => undefined);
        refrescar();
      } catch (e) {
        if (cancelado) return;
        const status = (e as { status?: number })?.status;
        setEstado(status === 503 ? 'sin-configurar' : 'error');
      }
    })();

    return () => {
      cancelado = true;
      room.removeAllListeners();
      void room.disconnect();
      audiosRef.current.forEach((els) => els.forEach((e) => e.remove()));
      audiosRef.current.clear();
      roomRef.current = null;
      setEstado('apagada');
      setHablando(new Set());
    };
  }, [callId, pedirPase, refrescar]);

  const room = roomRef.current;
  const yo = room?.localParticipant;
  const micPrendido = !!yo?.isMicrophoneEnabled;
  const camPrendida = !!yo?.isCameraEnabled;
  const pantallaCompartida = !!yo?.isScreenShareEnabled;
  const participantes: Participant[] = room ? [room.localParticipant, ...Array.from(room.remoteParticipants.values())] : [];

  const alternar = useCallback(async (que: 'mic' | 'cam' | 'pantalla') => {
    const lp = roomRef.current?.localParticipant;
    if (!lp) return;
    try {
      if (que === 'mic') await lp.setMicrophoneEnabled(!lp.isMicrophoneEnabled);
      else if (que === 'cam') await lp.setCameraEnabled(!lp.isCameraEnabled);
      else await lp.setScreenShareEnabled(!lp.isScreenShareEnabled);
    } catch { /* permiso denegado: queda como estaba */ }
    refrescar();
  }, [refrescar]);

  const activarAudio = useCallback(async () => {
    await roomRef.current?.startAudio().catch(() => undefined);
    setAudioBloqueado(!(roomRef.current?.canPlaybackAudio ?? true));
  }, []);

  return { estado, participantes, hablando, micPrendido, camPrendida, pantallaCompartida, audioBloqueado, alternar, activarAudio };
}
